interface LogLevel {
  DEBUG: 'debug';
  INFO: 'info';
  WARN: 'warn';
  ERROR: 'error';
  TRACE: 'trace';
}

interface EnvironmentSnapshot {
  browser: {
    userAgent: string;
    language: string;
    platform: string;
    viewport: { width: number; height: number };
    online: boolean;
  };
  route: {
    pathname: string;
    search: string;
    hash: string;
  };
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
    usagePercent: number;
  };
  network?: {
    effectiveType: string;
    downlink: number;
    rtt: number;
    saveData: boolean;
  };
  performance: {
    navigationTiming: number;
    domContentLoaded: number;
  };
  session: {
    sessionStart: number;
    sessionDuration: number;
  };
}

interface ComponentHierarchy {
  component: string;
  parent?: string;
  props?: Record<string, any>;
  depth: number;
}

interface CausalityEvent {
  id: string;
  correlationId: string;
  timestamp: number;
  eventType: string;
  description: string;
  data?: any;
  triggeredBy?: string;
}

interface LogEntry {
  timestamp: string;
  level: keyof LogLevel;
  category: string;
  message: string;
  data?: any;
  stackTrace?: string;
  context?: Record<string, any>;
  correlationId?: string;
  environmentSnapshot?: EnvironmentSnapshot;
  componentHierarchy?: ComponentHierarchy[];
  causedBy?: string;
  aiAnnotations?: {
    severity: 'critical' | 'high' | 'medium' | 'low';
    category: string;
    suggestedAction?: string;
    relatedLogs?: string[];
    pattern?: string;
  };
}

interface LoggerConfig {
  enabled: boolean;
  level: keyof LogLevel;
  categories: string[];
  persistToStorage: boolean;
  maxStorageEntries: number;
  consoleOutput: boolean;
  remoteLogging: boolean;
  includeStackTrace: boolean;
  groupSimilarLogs: boolean;
}

class GlobalLogger {
  private static instance: GlobalLogger;
  private config: LoggerConfig;
  private logBuffer: LogEntry[] = [];
  private logGroups: Map<string, { count: number; lastLog: LogEntry }> = new Map();
  private performanceMarks: Map<string, number> = new Map();
  private dataFlowTracker: Map<string, any[]> = new Map();
  private correlationIdStack: string[] = [];
  private causalityChains: Map<string, CausalityEvent[]> = new Map();
  private componentStack: ComponentHierarchy[] = [];
  private sessionStartTime: number = Date.now();

  private readonly LOG_LEVELS: Record<keyof LogLevel, number> = {
    TRACE: 0,
    DEBUG: 1,
    INFO: 2,
    WARN: 3,
    ERROR: 4
  };

  private readonly LOG_COLORS = {
    TRACE: '#666666',
    DEBUG: '#7c3aed',
    INFO: '#0ea5e9',
    WARN: '#f59e0b',
    ERROR: '#ef4444'
  };

  private constructor() {
    this.config = this.loadConfig();
    this.setupGlobalErrorHandlers();
    this.restoreFromStorage();
  }

  static getInstance(): GlobalLogger {
    if (!GlobalLogger.instance) {
      GlobalLogger.instance = new GlobalLogger();
    }
    return GlobalLogger.instance;
  }

  private loadConfig(): LoggerConfig {
    const stored = localStorage.getItem('logger-config');
    const defaults: LoggerConfig = {
      enabled: false,
      level: 'DEBUG',
      categories: ['*'],
      persistToStorage: true,
      maxStorageEntries: 1000,
      consoleOutput: true,
      remoteLogging: false,
      includeStackTrace: true,
      groupSimilarLogs: true
    };

    if (stored) {
      try {
        return { ...defaults, ...JSON.parse(stored) };
      } catch {
        return defaults;
      }
    }
    return defaults;
  }

  updateConfig(partial: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...partial };
    localStorage.setItem('logger-config', JSON.stringify(this.config));
  }

  isEnabled(): boolean {
    return this.config.enabled;
  }

  private generateCorrelationId(): string {
    return `corr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  startCorrelation(description: string, metadata?: any): string {
    if (!this.config.enabled) return '';
    
    const correlationId = this.generateCorrelationId();
    this.correlationIdStack.push(correlationId);
    
    this.trackCausalityEvent({
      id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      correlationId,
      timestamp: Date.now(),
      eventType: 'correlation-start',
      description,
      data: metadata
    });
    
    this.debug('CORRELATION', `Started correlation: ${description}`, { correlationId, metadata });
    return correlationId;
  }

  endCorrelation(correlationId?: string): void {
    if (!this.config.enabled) return;
    
    const id = correlationId || this.correlationIdStack.pop();
    if (!id) return;
    
    if (!correlationId) {
      this.correlationIdStack = this.correlationIdStack.filter(cid => cid !== id);
    }
    
    this.trackCausalityEvent({
      id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      correlationId: id,
      timestamp: Date.now(),
      eventType: 'correlation-end',
      description: 'Correlation ended'
    });
    
    this.debug('CORRELATION', `Ended correlation: ${id}`);
  }

  getCurrentCorrelationId(): string | undefined {
    if (!this.config.enabled) return undefined;
    return this.correlationIdStack[this.correlationIdStack.length - 1];
  }

  private trackCausalityEvent(event: CausalityEvent): void {
    if (!this.config.enabled) return;
    
    if (!this.causalityChains.has(event.correlationId)) {
      this.causalityChains.set(event.correlationId, []);
    }
    
    const chain = this.causalityChains.get(event.correlationId)!;
    chain.push(event);
  }

  getCausalityChain(correlationId: string): CausalityEvent[] {
    if (!this.config.enabled) return [];
    return this.causalityChains.get(correlationId) || [];
  }

  pushComponent(component: string, props?: Record<string, any>, parent?: string): void {
    if (!this.config.enabled) return;
    
    this.componentStack.push({
      component,
      parent: parent || this.componentStack[this.componentStack.length - 1]?.component,
      props,
      depth: this.componentStack.length
    });
  }

  popComponent(): void {
    if (!this.config.enabled) return;
    this.componentStack.pop();
  }

  getComponentHierarchy(): ComponentHierarchy[] {
    if (!this.config.enabled) return [];
    return [...this.componentStack];
  }

  private captureEnvironmentSnapshot(): EnvironmentSnapshot {
    const snapshot: EnvironmentSnapshot = {
      browser: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        online: navigator.onLine
      },
      route: {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
      },
      performance: {
        navigationTiming: performance.now(),
        domContentLoaded: performance.timing?.domContentLoadedEventEnd - performance.timing?.navigationStart || 0
      },
      session: {
        sessionStart: this.sessionStartTime,
        sessionDuration: Date.now() - this.sessionStartTime
      }
    };

    // @ts-ignore - performance.memory is not in standard TypeScript types
    if ('memory' in performance) {
      // @ts-ignore
      const memory = performance.memory as {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      };
      snapshot.memory = {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        usagePercent: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
      };
    }

    // @ts-ignore - connection is not in standard TypeScript types
    if ('connection' in navigator) {
      // @ts-ignore
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn) {
        snapshot.network = {
          effectiveType: conn.effectiveType || 'unknown',
          downlink: conn.downlink || 0,
          rtt: conn.rtt || 0,
          saveData: conn.saveData || false
        };
      }
    }

    return snapshot;
  }

  private generateAIAnnotations(level: keyof LogLevel, category: string, message: string, data?: any): LogEntry['aiAnnotations'] {
    const annotations: LogEntry['aiAnnotations'] = {
      severity: this.mapLevelToSeverity(level),
      category: category,
      pattern: this.detectPattern(category, message, data)
    };

    if (level === 'ERROR') {
      annotations.suggestedAction = this.suggestErrorAction(message, data);
    } else if (level === 'WARN') {
      annotations.suggestedAction = this.suggestWarningAction(message, data);
    }

    return annotations;
  }

  private mapLevelToSeverity(level: keyof LogLevel): 'critical' | 'high' | 'medium' | 'low' {
    switch (level) {
      case 'ERROR': return 'critical';
      case 'WARN': return 'high';
      case 'INFO': return 'medium';
      default: return 'low';
    }
  }

  private detectPattern(category: string, message: string, data?: any): string {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out')) {
      return 'TIMEOUT_ERROR';
    }
    if (lowerMessage.includes('network') || lowerMessage.includes('fetch failed')) {
      return 'NETWORK_ERROR';
    }
    if (lowerMessage.includes('null') || lowerMessage.includes('undefined')) {
      return 'NULL_REFERENCE_ERROR';
    }
    if (lowerMessage.includes('permission') || lowerMessage.includes('unauthorized')) {
      return 'PERMISSION_ERROR';
    }
    if (category === 'RENDER' && data?.duration > 100) {
      return 'SLOW_RENDER';
    }
    if (category === 'API' && data?.duration > 3000) {
      return 'SLOW_API_CALL';
    }
    
    return 'GENERAL';
  }

  private suggestErrorAction(message: string, data?: any): string {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('timeout')) {
      return 'Check network connectivity and server response time. Consider increasing timeout threshold.';
    }
    if (lowerMessage.includes('network') || lowerMessage.includes('fetch failed')) {
      return 'Verify network connection. Check if API endpoint is accessible. Review CORS configuration.';
    }
    if (lowerMessage.includes('null') || lowerMessage.includes('undefined')) {
      return 'Add null checks before accessing properties. Ensure data is loaded before rendering.';
    }
    if (lowerMessage.includes('permission') || lowerMessage.includes('unauthorized')) {
      return 'Check user authentication status. Verify user has required permissions. Review token expiry.';
    }
    
    return 'Review error stack trace. Check recent code changes. Look for similar patterns in logs.';
  }

  private suggestWarningAction(message: string, data?: any): string {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('slow') || lowerMessage.includes('performance')) {
      return 'Profile the operation. Check for unnecessary re-renders. Consider memoization or caching.';
    }
    if (lowerMessage.includes('memory')) {
      return 'Check for memory leaks. Review event listener cleanup. Verify component unmount logic.';
    }
    if (lowerMessage.includes('cache')) {
      return 'Review cache invalidation strategy. Check cache size limits. Verify cache key uniqueness.';
    }
    
    return 'Monitor this warning. If it persists, investigate further.';
  }

  private setupGlobalErrorHandlers(): void {
    window.addEventListener('error', (event) => {
      this.error('GLOBAL', 'Uncaught error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.error('GLOBAL', 'Unhandled promise rejection', {
        reason: event.reason
      });
    });
  }

  private shouldLog(level: keyof LogLevel, category: string): boolean {
    if (!this.config.enabled) return false;
    
    if (this.LOG_LEVELS[level] < this.LOG_LEVELS[this.config.level]) {
      return false;
    }

    if (!this.config.categories.includes('*')) {
      return this.config.categories.some(cat => 
        category.toLowerCase().includes(cat.toLowerCase()) ||
        cat.toLowerCase().includes(category.toLowerCase())
      );
    }

    return true;
  }

  private formatMessage(level: keyof LogLevel, category: string, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const color = this.LOG_COLORS[level];
    
    let formatted = `%c[${timestamp.split('T')[1].split('.')[0]}] [${level}] [${category}]%c ${message}`;
    
    if (data !== undefined) {
      if (typeof data === 'object') {
        formatted += '\n' + JSON.stringify(data, null, 2);
      } else {
        formatted += ` | ${data}`;
      }
    }
    
    return formatted;
  }

  private getStackTrace(): string {
    const stack = new Error().stack || '';
    const lines = stack.split('\n');
    return lines.slice(3).join('\n');
  }

  private log(level: keyof LogLevel, category: string, message: string, data?: any, context?: Record<string, any>): void {
    if (!this.shouldLog(level, category)) return;

    const correlationId = this.getCurrentCorrelationId();
    const environmentSnapshot = level === 'ERROR' || level === 'WARN' ? this.captureEnvironmentSnapshot() : undefined;
    const componentHierarchy = level === 'ERROR' ? this.getComponentHierarchy() : undefined;
    const aiAnnotations = this.generateAIAnnotations(level, category, message, data);

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      context,
      correlationId,
      environmentSnapshot,
      componentHierarchy,
      aiAnnotations,
      stackTrace: this.config.includeStackTrace && level === 'ERROR' ? this.getStackTrace() : undefined
    };

    if (correlationId) {
      this.trackCausalityEvent({
        id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        correlationId,
        timestamp: Date.now(),
        eventType: `log-${level.toLowerCase()}`,
        description: `${category}: ${message}`,
        data
      });
    }

    if (this.config.groupSimilarLogs) {
      const key = `${level}-${category}-${message}`;
      const existing = this.logGroups.get(key);
      if (existing && Date.now() - new Date(existing.lastLog.timestamp).getTime() < 1000) {
        existing.count++;
        existing.lastLog = entry;
        return;
      } else {
        this.logGroups.set(key, { count: 1, lastLog: entry });
      }
    }

    this.logBuffer.push(entry);
    
    if (this.config.persistToStorage) {
      this.persistToStorage(entry);
    }

    if (this.config.consoleOutput) {
      const formatted = this.formatMessage(level, category, message, data);
      const color = this.LOG_COLORS[level];
      
      switch (level) {
        case 'ERROR':
          console.error(formatted, `color: ${color}; font-weight: bold`, 'color: inherit', data);
          break;
        case 'WARN':
          console.warn(formatted, `color: ${color}; font-weight: bold`, 'color: inherit', data);
          break;
        case 'INFO':
          console.info(formatted, `color: ${color}; font-weight: bold`, 'color: inherit', data);
          break;
        default:
          console.log(formatted, `color: ${color}; font-weight: bold`, 'color: inherit', data);
      }
    }
  }

  trace(category: string, message: string, data?: any, context?: Record<string, any>): void {
    this.log('TRACE', category, message, data, context);
  }

  debug(category: string, message: string, data?: any, context?: Record<string, any>): void {
    this.log('DEBUG', category, message, data, context);
  }

  info(category: string, message: string, data?: any, context?: Record<string, any>): void {
    this.log('INFO', category, message, data, context);
  }

  warn(category: string, message: string, data?: any, context?: Record<string, any>): void {
    this.log('WARN', category, message, data, context);
  }

  error(category: string, message: string, data?: any, context?: Record<string, any>): void {
    this.log('ERROR', category, message, data, context);
  }

  startPerformance(label: string): void {
    if (!this.config.enabled) return;
    this.performanceMarks.set(label, performance.now());
    this.trace('PERFORMANCE', `Started measuring: ${label}`);
  }

  endPerformance(label: string): number {
    if (!this.config.enabled) return 0;
    
    const start = this.performanceMarks.get(label);
    if (!start) {
      this.warn('PERFORMANCE', `No start mark found for: ${label}`);
      return 0;
    }
    
    const duration = performance.now() - start;
    this.performanceMarks.delete(label);
    this.info('PERFORMANCE', `${label} completed`, { duration: `${duration.toFixed(2)}ms` });
    return duration;
  }

  trackDataFlow(flowId: string, stage: string, data: any): void {
    if (!this.config.enabled) return;
    
    if (!this.dataFlowTracker.has(flowId)) {
      this.dataFlowTracker.set(flowId, []);
    }
    
    const flow = this.dataFlowTracker.get(flowId)!;
    flow.push({
      stage,
      timestamp: Date.now(),
      data: typeof data === 'object' ? { ...data } : data
    });
    
    this.debug('DATA_FLOW', `${flowId} - ${stage}`, data);
  }

  getDataFlow(flowId: string): any[] {
    return this.dataFlowTracker.get(flowId) || [];
  }

  clearDataFlow(flowId: string): void {
    if (!this.config.enabled) return;
    this.dataFlowTracker.delete(flowId);
  }

  logApiCall(method: string, url: string, params?: any, response?: any, error?: any): void {
    if (!this.config.enabled) return;
    
    const logData = {
      method,
      url,
      params,
      response: response ? (response.length > 1000 ? `${response.length} bytes` : response) : undefined,
      error
    };

    if (error) {
      this.error('API', `${method} ${url} failed`, logData);
    } else {
      this.debug('API', `${method} ${url}`, logData);
    }
  }

  logComponentLifecycle(component: string, event: string, props?: any): void {
    if (!this.config.enabled) return;
    this.trace('COMPONENT', `${component} - ${event}`, props);
  }

  logComponentRender(component: string, renderCount: number, props?: any): void {
    if (!this.config.enabled) return;
    this.trace('RENDER', `${component} rendered (count: ${renderCount})`, props);
  }

  logComponentMount(component: string, props?: any): void {
    if (!this.config.enabled) return;
    this.debug('LIFECYCLE', `${component} mounted`, props);
  }

  logComponentUnmount(component: string): void {
    if (!this.config.enabled) return;
    this.debug('LIFECYCLE', `${component} unmounted`);
  }

  logWindowEvent(eventType: string, windowName: string, data?: any): void {
    if (!this.config.enabled) return;
    this.info('WINDOW', `${windowName} - ${eventType}`, data);
  }

  logViewRefresh(viewName: string, reason?: string, data?: any): void {
    if (!this.config.enabled) return;
    this.info('VIEW_REFRESH', `${viewName} refreshed${reason ? `: ${reason}` : ''}`, data);
  }

  logTableRefresh(tableName: string, reason?: string, rowCount?: number): void {
    if (!this.config.enabled) return;
    this.info('TABLE_REFRESH', `${tableName} refreshed${reason ? `: ${reason}` : ''}`, { rowCount });
  }

  logStateChange(component: string, stateName: string, oldValue: any, newValue: any): void {
    if (!this.config.enabled) return;
    
    this.debug('STATE', `${component}.${stateName} changed`, {
      old: oldValue,
      new: newValue,
      diff: this.getDiff(oldValue, newValue)
    });
  }

  private getDiff(oldVal: any, newVal: any): any {
    if (typeof oldVal !== 'object' || typeof newVal !== 'object') {
      return { changed: oldVal !== newVal };
    }

    const diff: any = {};
    const allKeys = [...Object.keys(oldVal || {}), ...Object.keys(newVal || {})];
    const uniqueKeys = Array.from(new Set(allKeys));
    
    for (const key of uniqueKeys) {
      if (oldVal?.[key] !== newVal?.[key]) {
        diff[key] = {
          old: oldVal?.[key],
          new: newVal?.[key]
        };
      }
    }
    
    return diff;
  }

  private persistToStorage(entry: LogEntry): void {
    try {
      const stored = localStorage.getItem('logger-entries');
      let entries: LogEntry[] = stored ? JSON.parse(stored) : [];
      
      entries.push(entry);
      
      if (entries.length > this.config.maxStorageEntries) {
        entries = entries.slice(-this.config.maxStorageEntries);
      }
      
      localStorage.setItem('logger-entries', JSON.stringify(entries));
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        try {
          localStorage.removeItem('logger-entries');
          localStorage.setItem('logger-entries', JSON.stringify([entry]));
          console.warn('Cleared logger storage due to quota exceeded, starting fresh');
        } catch (clearError) {
          console.error('Failed to clear and restart logger storage', clearError);
        }
      } else {
        console.error('Failed to persist log entry', e);
      }
    }
  }

  private restoreFromStorage(): void {
    if (!this.config.persistToStorage) return;
    
    try {
      const stored = localStorage.getItem('logger-entries');
      if (stored) {
        this.logBuffer = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to restore logs from storage', e);
    }
  }

  getLogs(filter?: { level?: keyof LogLevel; category?: string; since?: Date; correlationId?: string }): LogEntry[] {
    let logs = [...this.logBuffer];
    
    if (filter?.level) {
      logs = logs.filter(log => this.LOG_LEVELS[log.level] >= this.LOG_LEVELS[filter.level!]);
    }
    
    if (filter?.category) {
      logs = logs.filter(log => log.category.includes(filter.category!));
    }
    
    if (filter?.since) {
      const sinceTime = filter.since.getTime();
      logs = logs.filter(log => new Date(log.timestamp).getTime() >= sinceTime);
    }

    if (filter?.correlationId) {
      logs = logs.filter(log => log.correlationId === filter.correlationId);
    }
    
    return logs;
  }

  clearLogs(): void {
    this.logBuffer = [];
    this.logGroups.clear();
    this.performanceMarks.clear();
    this.dataFlowTracker.clear();
    this.causalityChains.clear();
    this.componentStack = [];
    this.correlationIdStack = [];
    localStorage.removeItem('logger-entries');
  }

  exportLogs(): string {
    return JSON.stringify(this.logBuffer, null, 2);
  }

  exportWithAIAnalysis(): string {
    const analysis = {
      exportDate: new Date().toISOString(),
      summary: {
        totalLogs: this.logBuffer.length,
        byLevel: this.groupByLevel(),
        byCategory: this.groupByCategory(),
        errorPatterns: this.detectErrorPatterns(),
        correlations: this.buildCorrelationSummary()
      },
      causalityGraphs: this.buildCausalityGraphs(),
      logs: this.logBuffer,
      aiInsights: {
        criticalIssues: this.identifyCriticalIssues(),
        performanceBottlenecks: this.identifyPerformanceBottlenecks(),
        errorClusters: this.clusterErrors(),
        recommendations: this.generateRecommendations()
      }
    };

    return JSON.stringify(analysis, null, 2);
  }

  private groupByLevel(): Record<string, number> {
    return this.logBuffer.reduce((acc, log) => {
      acc[log.level] = (acc[log.level] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  private groupByCategory(): Record<string, number> {
    return this.logBuffer.reduce((acc, log) => {
      acc[log.category] = (acc[log.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  private detectErrorPatterns(): Record<string, number> {
    return this.logBuffer
      .filter(log => log.aiAnnotations?.pattern)
      .reduce((acc, log) => {
        const pattern = log.aiAnnotations?.pattern;
        if (pattern) {
          acc[pattern] = (acc[pattern] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);
  }

  private buildCorrelationSummary(): any[] {
    const correlations: any[] = [];
    
    this.causalityChains.forEach((chain, correlationId) => {
      correlations.push({
        correlationId,
        eventCount: chain.length,
        duration: chain.length > 0 ? chain[chain.length - 1].timestamp - chain[0].timestamp : 0,
        events: chain.map(e => ({
          type: e.eventType,
          description: e.description,
          timestamp: e.timestamp
        }))
      });
    });
    
    return correlations;
  }

  private buildCausalityGraphs(): any[] {
    const graphs: any[] = [];
    
    this.causalityChains.forEach((chain, correlationId) => {
      const nodes = chain.map((event, index) => ({
        id: event.id,
        label: event.description,
        type: event.eventType,
        index
      }));
      
      const edges = chain.slice(1).map((event, index) => ({
        from: chain[index].id,
        to: event.id,
        duration: event.timestamp - chain[index].timestamp
      }));
      
      graphs.push({
        correlationId,
        nodes,
        edges
      });
    });
    
    return graphs;
  }

  private identifyCriticalIssues(): any[] {
    return this.logBuffer
      .filter(log => log.aiAnnotations?.severity === 'critical')
      .map(log => ({
        timestamp: log.timestamp,
        category: log.category,
        message: log.message,
        pattern: log.aiAnnotations?.pattern,
        suggestedAction: log.aiAnnotations?.suggestedAction,
        correlationId: log.correlationId
      }));
  }

  private identifyPerformanceBottlenecks(): any[] {
    return this.logBuffer
      .filter(log => 
        log.aiAnnotations?.pattern === 'SLOW_RENDER' || 
        log.aiAnnotations?.pattern === 'SLOW_API_CALL'
      )
      .map(log => ({
        timestamp: log.timestamp,
        operation: log.message,
        duration: log.data?.duration,
        pattern: log.aiAnnotations?.pattern
      }));
  }

  private clusterErrors(): any[] {
    const clusters: Record<string, any[]> = {};
    
    this.logBuffer
      .filter(log => log.level === 'ERROR')
      .forEach(log => {
        const pattern = log.aiAnnotations?.pattern || 'UNKNOWN';
        if (!clusters[pattern]) {
          clusters[pattern] = [];
        }
        clusters[pattern].push({
          timestamp: log.timestamp,
          message: log.message,
          correlationId: log.correlationId
        });
      });
    
    return Object.entries(clusters).map(([pattern, errors]) => ({
      pattern,
      count: errors.length,
      errors: errors.slice(0, 5)
    }));
  }

  private generateRecommendations(): string[] {
    const recommendations: string[] = [];
    const patterns = this.detectErrorPatterns();
    
    if (patterns['TIMEOUT_ERROR'] > 3) {
      recommendations.push('Multiple timeout errors detected. Consider increasing timeout thresholds or optimizing server response times.');
    }
    
    if (patterns['NETWORK_ERROR'] > 3) {
      recommendations.push('Network errors are frequent. Implement retry logic with exponential backoff.');
    }
    
    if (patterns['NULL_REFERENCE_ERROR'] > 2) {
      recommendations.push('Null reference errors found. Add defensive null checks and use optional chaining.');
    }
    
    if (patterns['SLOW_RENDER'] > 5) {
      recommendations.push('Multiple slow renders detected. Profile components and implement React.memo or useMemo.');
    }
    
    return recommendations;
  }

  createContextLogger(defaultCategory: string, defaultContext?: Record<string, any>) {
    return {
      trace: (message: string, data?: any) => this.trace(defaultCategory, message, data, defaultContext),
      debug: (message: string, data?: any) => this.debug(defaultCategory, message, data, defaultContext),
      info: (message: string, data?: any) => this.info(defaultCategory, message, data, defaultContext),
      warn: (message: string, data?: any) => this.warn(defaultCategory, message, data, defaultContext),
      error: (message: string, data?: any) => this.error(defaultCategory, message, data, defaultContext)
    };
  }
}

export const logger = GlobalLogger.getInstance();

export const createLogger = (category: string, context?: Record<string, any>) => {
  return logger.createContextLogger(category, context);
};
